import { QuickAddComponent } from '@theme/quick-add';

/**
 * `<echt-pair-quick-add-component>` — the "Pair with" tile's ADD TO CART.
 *
 * Basil, 8 Sep 2026: "can we also fix these make the add to cart button bring up
 * the quick add to select size there and make hovering over these showcase the
 * size selector as well like everywhere else."
 *
 * So the tile no longer carries its own `SIZE:` select and its own
 * `<form action="/cart/add">`; the size is chosen in the theme's quick-add
 * dialog, exactly as it is from a collection card. Which means the ~200 lines
 * this file used to hold — a near-copy of `assets/product-form.js`'s add path,
 * with its own `CartLinesUpdateEvent` contract, its own 429 handling and its own
 * double-submit guard — are gone. Nothing about the dialog is reimplemented
 * here. The whole file is one subclass and one overridden getter.
 *
 * WHY A SUBCLASS AT ALL. `QuickAddComponent.handleClick` fetches the product
 * page, morphs `[data-product-grid-content]` into `#quick-add-modal-content` and
 * shows `#quick-add-dialog` — all of which works anywhere on the page. The one
 * thing that does not is where it gets the URL to fetch: its `productPageUrl`
 * getter reads the href off a `<product-card>` (or `<product-hotspot-component>`)
 * ancestor, and a Pair with tile is neither. Overriding that one getter to read
 * a data attribute is the smallest possible adaptation; every other code path in
 * the stock component is inherited untouched.
 *
 * The stock paths that DO run in here, and are safe:
 *   - `#getSelectedVariantId()` returns null (no `<product-card>`), so
 *     `#syncVariantSelection` returns early and the dialog opens on the paired
 *     product's own default variant — which is what we want, since the tile
 *     offers no swatches that could have preselected anything.
 *   - `#handleProductSelectUpdate` returns at its `data-renders-both-buttons`
 *     guard, which the tile deliberately does not set: there is one control and
 *     it never flips between "Add" and "Choose".
 *   - `data-uses-selling-plans="true"` still sends subscriptions to the product
 *     page instead of the dialog, because the subscription widget only runs
 *     there.
 *
 * The tag ends in `-component` on purpose: `assets/component.js` only runs its
 * `customElements.upgrade()` race-fix for elements whose tag ends that way, and
 * this module is loaded from inside the block (so a click can land before it has
 * parsed).
 *
 * The hover-size-bar half of the same ask needed no JavaScript of its own —
 * `assets/echt-card-sizes.js` now recognises the tile as a card. See there.
 */
class EchtPairQuickAdd extends QuickAddComponent {
  /**
   * The paired product's page, which stock reads off a `<product-card>`.
   *
   * Rendered by `snippets/echt-style-with-tile.liquid` as `data-product-url`.
   * Empty string rather than undefined on a missing attribute, because that is
   * what stock's getter returns when there is no link, and `handleClick` already
   * copes with it (it fetches nothing and opens the dialog rather than throwing).
   *
   * @returns {string} The product page URL.
   */
  get productPageUrl() {
    return this.dataset.productUrl || '';
  }
}

if (!customElements.get('echt-pair-quick-add-component')) {
  customElements.define('echt-pair-quick-add-component', EchtPairQuickAdd);
}
